import { Stack, Typography, useTheme } from "@mui/material";

import { ErrorOutlineRounded } from "@mui/icons-material";

import { ChevronRightIcon } from "igfd-ui/src";

import { staffMenuCopy } from "../../../menu/staff-menu-copy";
import { INDENT_STEP } from "../../../menu/staff-menu-tree";

interface GroupRowProps {
  /** Null when the header record is unusable. */
  title: string | null;
  description: string;
  required: boolean;
  optionCount: number;
  groupCount: number;
  indent: number;
  hasDivider: boolean;
  onClick: () => void;
}

/**
 * A choice group with no ancestor dish to fold into.
 *
 * It opens its options rather than listing them: Frankie's Wings offers three
 * sizes and five flavours, and spending eight menu rows on one orderable thing
 * buries the dish a waiter is scanning for.
 */
export const GroupRow = ({
  title,
  description,
  required,
  optionCount,
  groupCount,
  indent,
  hasDivider,
  onClick,
}: GroupRowProps) => {
  const {
    palette: { oscar },
  } = useTheme();

  if (!title) {
    return (
      <Stack
        alignItems="center"
        direction="row"
        gap={0.5}
        sx={{ paddingLeft: indent * INDENT_STEP, paddingY: 3 }}
        borderBottom={
          hasDivider ? `1px solid ${oscar.light.borders.gray}` : undefined
        }
      >
        <ErrorOutlineRounded
          fontSize="small"
          sx={{ color: oscar.light.content.danger }}
        />
        <Typography
          variant="labelRMedium"
          color={oscar.light.content.dangerText}
        >
          {staffMenuCopy.groupUnreadableTitle}
        </Typography>
      </Stack>
    );
  }

  return (
    <Stack
      alignItems="flex-start"
      direction="row"
      justifyContent="space-between"
      sx={{ cursor: "pointer", paddingLeft: indent * INDENT_STEP }}
      paddingY={3}
      borderBottom={
        hasDivider ? `1px solid ${oscar.light.borders.gray}` : undefined
      }
      onClick={onClick}
    >
      <Stack gap={1} minWidth={0}>
        <Typography variant="labelLMedium">{title}</Typography>
        {description ? (
          <Typography variant="bodyRLight">{description}</Typography>
        ) : null}
        <Typography variant="labelS" color={oscar.light.content.content1}>
          {staffMenuCopy.groupMeta(required, optionCount, groupCount)}
        </Typography>
      </Stack>
      <ChevronRightIcon
        height={16}
        width={16}
        color={oscar.light.content.link}
      />
    </Stack>
  );
};
